/**
 * Noticing that a newer build has been deployed under an open tab.
 *
 * A single-page app loads its code once. Without this, a tab opened before a deploy keeps running the old
 * bundle — old menus, old screens — until someone thinks to hard-refresh. The build stamps its id into the
 * bundle (`__BUILD_ID__`) and writes the same id to `/version.json` beside it (see vite.config.ts); the tab
 * compares the two now and then and, once they differ, reloads at the next page change or on request.
 */

export const RUNNING_BUILD_ID: string = __BUILD_ID__;

/** Remembers which build we already reloaded for, so a stale edge copy of index.html can't reload-loop us. */
const RELOADED_FOR_KEY = "kd.reloadedForBuild";

/** The id of the build currently deployed, or null when it can't be read (offline, dev server, a 404). */
export async function fetchDeployedBuildId(): Promise<string | null> {
  try {
    // no-store plus a throwaway query: this answer is worthless if any cache along the way supplies it.
    const res = await fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" });
    if (!res.ok) return null;
    const body: unknown = await res.json();
    const id = (body as { build?: unknown } | null)?.build;
    return typeof id === "string" && id.length > 0 ? id : null;
  } catch {
    return null;
  }
}

/** Whether the deployed build is a different one from the build this tab is running. */
export function isNewerBuild(running: string, deployed: string | null): boolean {
  return deployed !== null && deployed !== running;
}

/**
 * Whether reloading on our own is safe. False once we've already reloaded for this deployed build and still
 * came back running the old one — reloading again would only repeat that, so leave it to the person.
 */
export function mayAutoReload(deployed: string, reloadedFor: string | null): boolean {
  return reloadedFor !== deployed;
}

export function reloadedForBuild(): string | null {
  try {
    return sessionStorage.getItem(RELOADED_FOR_KEY);
  } catch {
    return null;
  }
}

export function reloadForBuild(deployed: string): void {
  try {
    sessionStorage.setItem(RELOADED_FOR_KEY, deployed);
  } catch {
    /* storage blocked — the reload still happens, only the loop guard is lost */
  }
  window.location.reload();
}
